import React, { useEffect, useState } from 'react';
import { GraduationCap, ExternalLink, CheckCircle, AlertCircle } from 'lucide-react';
import { getUsernameFromProfileId, getCoachByPlayerProfile } from '../../../../services/firestoreService';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

/**
 * CoachingEditor - Editor component for the coaching block
 * Shows status info and link to manage lessons
 */
const CoachingEditor = ({ block, onChange, profileId, theme, hideStylePanels }) => {
  const { content } = block;
  const [username, setUsername] = useState(null);
  const [isCoachingEnabled, setIsCoachingEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCoachingInfo = async () => {
      if (!profileId) {
        setLoading(false);
        return;
      }

      try {
        // Get username for admin link
        const fetchedUsername = await getUsernameFromProfileId(profileId);
        setUsername(fetchedUsername);

        // Check if coaching is enabled by looking for a coach profile with this playerProfileId
        const coach = await getCoachByPlayerProfile(profileId);
        setIsCoachingEnabled(!!coach);
      } catch (error) {
        console.error('Error loading coaching info:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCoachingInfo();
  }, [profileId]);

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  const features = BLOCK_FEATURES['coaching'];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-400">
        <div className="animate-spin w-5 h-5 border-2 border-gray-600 border-t-[#749555] rounded-full mr-2"></div>
        Loading...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status Card */}
      <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-[#749555]/20 rounded-lg">
            <GraduationCap size={24} className="text-[#749555]" />
          </div>
          <div className="flex-1">
            <h4 className="text-white font-medium mb-1">Coaching Page</h4>
            {isCoachingEnabled ? (
              <div className="flex items-center gap-2 text-sm text-green-400">
                <CheckCircle size={14} />
                <span>Coaching enabled - Your lessons, pricing, and FAQ will display here</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-amber-400">
                <AlertCircle size={14} />
                <span>Set up coaching in your admin to display content</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Preview Info */}
      <div className="text-sm text-gray-400 space-y-2">
        <p>This block will display:</p>
        <ul className="list-disc list-inside space-y-1 text-gray-500">
          <li>Upcoming lesson carousel</li>
          <li>Your hero title and description</li>
          <li>Session pricing cards</li>
          <li>FAQ section</li>
          <li>Get started call-to-action</li>
        </ul>
      </div>

      {/* Start Expanded Toggle */}
      <div className="flex items-center justify-between p-3 bg-[#1a1a2e] border border-gray-700 rounded-lg">
        <div>
          <label className="text-sm font-medium text-gray-300">
            Start Expanded
          </label>
          <p className="text-xs text-gray-500 mt-0.5">
            Show full coaching section by default on your profile page
          </p>
        </div>
        <button
          type="button"
          onClick={() => onChange({ startExpanded: !content.startExpanded })}
          className={`relative w-11 h-6 rounded-full transition-colors ${
            content.startExpanded
              ? 'bg-[#749555]'
              : 'bg-gray-600'
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
              content.startExpanded
                ? 'translate-x-5'
                : 'translate-x-0'
            }`}
          />
        </button>
      </div>

      {/* Style Panels */}
      {!hideStylePanels && (<>
        <SizeSpacingPanel
          style={content.style || {}}
          onChange={handleStyleChange}
          features={features}
        />
        <ColorsFontsPanel
          style={content.style || {}}
          onChange={handleStyleChange}
          features={features}
          theme={theme || {}}
          blockType="coaching"
        />
      </>)}

      {/* Manage Lessons Link */}
      {username && (
        <a
          href={`/coach/${username}/admin`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#749555] hover:bg-[#8bab6a] text-white rounded-lg transition-colors text-sm font-medium"
        >
          Manage Lessons
          <ExternalLink size={14} />
        </a>
      )}
    </div>
  );
};

export default CoachingEditor;
